import { EvidencePicker } from '@/features/staff-bank/EvidencePicker';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '@/api/client';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { SelectField, TextAreaField, TextField } from '@/components/ui/Field';
import { PageHeader } from '@/components/ui/PageHeader';
import { LoadingLine } from '@/components/ui/States';
import { useToast } from '@/components/ui/Toast';
import { StaffOnly } from '@/features/admin-content/StaffOnly';
import {
  useCan,
  useCanEditContent,
  useCurrentUser,
} from '@/features/admin-content/use-current-user';
import { hasTaskType } from '@pmprep/exam-engine';
import { ItemHost } from '@/features/reading/ItemHost';
import { ActivityHistory } from '@/features/staff-workspace/ActivityHistory';
import { TimingEditor } from '@/features/staff-bank/TimingEditor';
import { AudioUpload } from '@/features/staff-workspace/AudioUpload';
import { SchemaFields, emptyFromSchema } from '@/features/staff-bank/schema-fields';
import { authClient } from '@/lib/auth-client';

export const Route = createFileRoute('/admin/bank/$stimulusId')({
  component: BankStimulusPage,
});

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function BankStimulusPage() {
  const { stimulusId } = Route.useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const meQuery = useCurrentUser();
  const canRead = useCan('content.admin');
  const canWrite = useCanEditContent();
  const canPublish = useCan('content.publish');
  const actorId = meQuery.data?.id;

  const canHistory = useCan('activity.read');
  const detail = useQuery({
    queryKey: ['admin', 'bank', stimulusId],
    enabled: canRead,
    queryFn: () => api.get('/api/v1/admin/bank/stimuli/{id}', { params: { id: stimulusId } }),
  });
  const types = useQuery({
    queryKey: ['admin', 'bank', 'task-types'],
    enabled: canRead,
    queryFn: () => api.get('/api/v1/admin/bank/task-types'),
  });

  const bodyText = useMemo(() => {
    const body = detail.data?.body;
    if (body && typeof body === 'object' && 'text' in body && typeof body.text === 'string') {
      return body.text;
    }
    return '';
  }, [detail.data]);

  const [draftText, setDraftText] = useState<string | null>(null);
  const [taskTypeId, setTaskTypeId] = useState('');
  const skillFilter = (detail.data?.skills ?? []).join(',');
  const typesForStimulus = (types.data?.taskTypes ?? []).filter((type) =>
    skillFilter ? type.skills.some((skill) => skillFilter.split(',').includes(skill)) : true,
  );
  const selectedType =
    typesForStimulus.find((row) => row.id === taskTypeId) ??
    types.data?.taskTypes.find((row) => row.id === taskTypeId);

  useEffect(() => {
    if (taskTypeId || !types.data) return;
    const list = types.data.taskTypes.filter((type) =>
      skillFilter ? type.skills.some((skill) => skillFilter.split(',').includes(skill)) : true,
    );
    const first = list[0] ?? types.data.taskTypes[0];
    if (!first) return;
    setTaskTypeId(first.id);
    setPayload(emptyFromSchema(first.payloadSchema));
    setAnswerKey(emptyFromSchema(first.answerKeySchema));
  }, [taskTypeId, types.data, skillFilter]);
  const [payload, setPayload] = useState<Record<string, unknown>>({});
  const [answerKey, setAnswerKey] = useState<Record<string, unknown>>({});
  const [itemDrafts, setItemDrafts] = useState<Record<string, ItemDraft>>({});
  const [labelsDraft, setLabelsDraft] = useState<string[] | null>(null);
  const [variantDraft, setVariantDraft] = useState<string | null>(null);

  const saveStimulus = useMutation({
    mutationFn: () =>
      api.patch('/api/v1/admin/bank/stimuli/{id}', {
        params: { id: stimulusId },
        body: {
          bodyText: draftText ?? bodyText,
          ...(labelsDraft ? { labels: labelsDraft } : {}),
          ...(variantDraft !== null
            ? {
                examVariant:
                  variantDraft === 'ielts-academic' || variantDraft === 'ielts-general-training'
                    ? variantDraft
                    : null,
              }
            : {}),
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'bank', stimulusId] });
      toast({ message: 'Saved', tone: 'success' });
    },
    onError: () => {
      toast({ message: 'We could not save the text', tone: 'error' });
    },
  });

  const saveTimings = useMutation({
    mutationFn: (turnTimings: { startMs: number; endMs: number }[] | null) =>
      api.patch('/api/v1/admin/bank/stimuli/{id}', {
        params: { id: stimulusId },
        body: { turnTimings },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'bank', stimulusId] });
      toast({ message: 'Timings saved', tone: 'success' });
    },
    onError: () => {
      toast({ message: 'We could not save the timings', tone: 'error' });
    },
  });

  const addItem = useMutation({
    mutationFn: () =>
      api.post('/api/v1/admin/bank/stimuli/{id}/items', {
        params: { id: stimulusId },
        body: { taskTypeId, payload, answerKey },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'bank', stimulusId] });
      toast({ message: 'Question added', tone: 'success' });
    },
    onError: (error) => {
      toast({
        message: error instanceof ApiError ? 'Check the question fields' : 'We could not add that',
        tone: 'error',
      });
    },
  });

  const saveItem = useMutation({
    mutationFn: (itemId: string) => {
      const draft = itemDrafts[itemId];
      if (!draft) throw new Error('missing draft');
      return api.patch('/api/v1/admin/bank/items/{id}', {
        params: { id: itemId },
        body: {
          payload: draft.payload,
          answerKey: draft.answerKey,
          explanation: cleanExplanation(draft.explanation),
          difficulty: draft.difficulty === '' ? null : Number(draft.difficulty),
        },
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'bank', stimulusId] });
      toast({ message: 'Question saved', tone: 'success' });
    },
    onError: () => {
      toast({ message: 'We could not save that question', tone: 'error' });
    },
  });

  const reviewItem = useMutation({
    mutationFn: (input: { itemId: string; decision: 'approve' | 'reject' }) =>
      api.post('/api/v1/admin/bank/items/{id}/review', {
        params: { id: input.itemId },
        body: { decision: input.decision },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'bank', stimulusId] });
      toast({ message: 'Review saved', tone: 'success' });
    },
    onError: () => {
      toast({ message: 'The author cannot approve their own question', tone: 'error' });
    },
  });

  const submitItem = useMutation({
    mutationFn: (itemId: string) =>
      api.post('/api/v1/admin/bank/items/{id}/submit', { params: { id: itemId } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'bank', stimulusId] });
      toast({ message: 'Sent for review', tone: 'success' });
    },
    onError: () => {
      toast({ message: 'Only drafts can be sent for review', tone: 'error' });
    },
  });

  const publishItem = useMutation({
    mutationFn: (itemId: string) =>
      api.post('/api/v1/admin/bank/items/{id}/publish', { params: { id: itemId } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'bank', stimulusId] });
      toast({ message: 'Published for scored mocks', tone: 'success' });
    },
  });

  if (sessionPending || meQuery.isPending) return <LoadingLine>Loading</LoadingLine>;
  if (!session) {
    void navigate({ to: '/sign-in' });
    return null;
  }
  if (!canRead) return <StaffOnly />;
  if (detail.isPending) return <LoadingLine>Loading this stimulus</LoadingLine>;
  if (detail.isError || !detail.data) {
    return <Alert title="We could not load this">Go back to the bank and open it again.</Alert>;
  }

  const row = detail.data;
  const text = draftText ?? bodyText;

  return (
    <div className="grid gap-6">
      <PageHeader
        eyebrow="Workspace"
        title={row.title}
        description={`Version ${row.currentVersionNumber ?? 'draft'} · ${row.status}. Edits do not change what students already sat.`}
      />

      <Card padding="lg" className="grid gap-4">
        <TextAreaField
          label="Stimulus text"
          rows={10}
          value={text}
          onChange={(event) => {
            setDraftText(event.target.value);
          }}
        />
        <SelectField
          label="Used in"
          value={variantDraft ?? row.examVariant ?? ''}
          disabled={!canWrite}
          onChange={(event) => {
            setVariantDraft(event.target.value);
          }}
        >
          <option value="ielts-academic">IELTS Academic</option>
          <option value="ielts-general-training">IELTS General Training</option>
          <option value="">Both</option>
        </SelectField>
        <LabelsEditor
          tags={row.tags}
          draft={labelsDraft}
          onChange={setLabelsDraft}
          disabled={!canWrite}
        />
        {canWrite ? (
          <div>
            <Button
              type="button"
              loading={saveStimulus.isPending}
              onClick={() => {
                saveStimulus.mutate();
              }}
            >
              Save text
            </Button>
          </div>
        ) : null}
        {row.kind === 'audio' ||
        row.skills.includes('listening') ||
        row.skills.includes('speaking') ? (
          <AudioUpload stimulusId={stimulusId} media={row.media} canWrite={canWrite} />
        ) : null}
        {row.media?.status === 'ready' &&
        row.media.durationMs &&
        row.skills.includes('listening') ? (
          <TimingEditor
            mediaId={row.media.id}
            durationMs={row.media.durationMs}
            peaks={row.media.peaks}
            script={bodyText}
            saved={
              row.body && typeof row.body === 'object' && 'turnTimings' in row.body
                ? row.body.turnTimings
                : null
            }
            disabled={!canWrite}
            saving={saveTimings.isPending}
            onSave={(timings) => {
              saveTimings.mutate(timings);
            }}
          />
        ) : null}
      </Card>

      {row.itemGroups
        .flatMap((group) => group.items)
        .map((item) => {
          const type = types.data?.taskTypes.find((entry) => entry.id === item.taskTypeId);
          const draft = itemDrafts[item.id] ?? draftFromItem(item);
          const own = Boolean(item.createdById && actorId && item.createdById === actorId);
          return (
            <Card key={item.id} padding="lg" className="grid gap-4">
              <p className="text-small text-muted">
                {item.taskTypeId} · {item.status} · version {item.currentVersionNumber ?? 'draft'}
              </p>
              <SchemaFields
                schema={type?.payloadSchema}
                disabled={!canWrite}
                value={draft.payload}
                onChange={(next) => {
                  setItemDrafts((current) => ({
                    ...current,
                    [item.id]: { ...draft, payload: next },
                  }));
                }}
              />
              <SchemaFields
                schema={type?.answerKeySchema}
                value={draft.answerKey}
                onChange={(next) => {
                  setItemDrafts((current) => ({
                    ...current,
                    [item.id]: { ...draft, answerKey: next },
                  }));
                }}
              />
              <ExplanationFields
                skills={row.skills}
                passage={text}
                canWrite={canWrite}
                value={draft.explanation}
                difficulty={draft.difficulty}
                onChange={(explanation, difficulty) => {
                  setItemDrafts((current) => ({
                    ...current,
                    [item.id]: { ...draft, explanation, difficulty },
                  }));
                }}
              />
              <div className="rounded-card border border-border p-4">
                <p className="eyebrow text-muted">Student preview</p>
                {hasTaskType(item.taskTypeId) ? (
                  <ItemHost
                    taskTypeId={item.taskTypeId}
                    payload={draft.payload}
                    value={null}
                    onChange={() => undefined}
                    mode="review"
                    answerKey={draft.answerKey}
                  />
                ) : (
                  <p className="text-small text-muted">
                    This question type has no student renderer yet.
                  </p>
                )}
              </div>
              {canWrite ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    loading={saveItem.isPending}
                    onClick={() => {
                      saveItem.mutate(item.id);
                    }}
                  >
                    Save question
                  </Button>
                  {own && item.status === 'draft' ? (
                    <Button
                      type="button"
                      loading={submitItem.isPending}
                      onClick={() => {
                        submitItem.mutate(item.id);
                      }}
                    >
                      Send for review
                    </Button>
                  ) : null}
                  {!own ? (
                    <Button
                      type="button"
                      onClick={() => {
                        reviewItem.mutate({ itemId: item.id, decision: 'approve' });
                      }}
                    >
                      Approve
                    </Button>
                  ) : null}
                  {canPublish && item.status === 'pilot' ? (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        publishItem.mutate(item.id);
                      }}
                    >
                      Publish for mocks
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </Card>
          );
        })}

      {canWrite ? (
        <Card padding="lg" className="grid gap-4">
          <h2 className="font-display text-h3">Add a question</h2>
          <SelectField
            label="Task type"
            value={taskTypeId}
            onChange={(event) => {
              const next = event.target.value;
              setTaskTypeId(next);
              const type = types.data?.taskTypes.find((entry) => entry.id === next);
              setPayload(emptyFromSchema(type?.payloadSchema));
              setAnswerKey(emptyFromSchema(type?.answerKeySchema));
            }}
          >
            {typesForStimulus.map((type) => (
              <option key={type.id} value={type.id}>
                {type.label}
              </option>
            ))}
          </SelectField>
          <SchemaFields
            schema={selectedType?.payloadSchema}
            value={payload}
            onChange={setPayload}
          />
          <SchemaFields
            schema={selectedType?.answerKeySchema}
            value={answerKey}
            onChange={setAnswerKey}
          />
          <div>
            <Button
              type="button"
              loading={addItem.isPending}
              onClick={() => {
                addItem.mutate();
              }}
            >
              Add question
            </Button>
          </div>
        </Card>
      ) : null}

      {canHistory ? (
        <section className="grid gap-3">
          <h2 className="font-display text-h3">History</h2>
          <ActivityHistory
            entityIds={[
              row.id,
              ...row.itemGroups.flatMap((group) => group.items.map((item) => item.id)),
            ]}
          />
        </section>
      ) : null}
    </div>
  );
}

interface ItemDraft {
  payload: Record<string, unknown>;
  answerKey: Record<string, unknown>;
  explanation: Record<string, unknown>;
  /** '' = not set. */
  difficulty: string;
}

function draftFromItem(item: {
  payload: unknown;
  answerKey: unknown;
  explanation: unknown;
  difficulty: number | null;
}): ItemDraft {
  return {
    payload: asRecord(item.payload),
    answerKey: asRecord(item.answerKey),
    explanation: asRecord(item.explanation),
    difficulty: item.difficulty === null ? '' : String(item.difficulty),
  };
}

/** Empty fields are dropped so a question with nothing written keeps no explanation at all. */
function cleanExplanation(value: Record<string, unknown>): Record<string, unknown> | undefined {
  const kept = Object.fromEntries(
    Object.entries(value).filter(([, v]) => !(typeof v === 'string' && v.trim() === '')),
  );
  return Object.keys(kept).length > 0 ? kept : undefined;
}

const SKILL_TAGS = ['listening', 'reading', 'writing', 'speaking'];

function LabelsEditor({
  tags,
  draft,
  onChange,
  disabled,
}: {
  tags: readonly string[];
  draft: string[] | null;
  onChange: (labels: string[]) => void;
  disabled: boolean;
}) {
  const labels = draft ?? tags.filter((tag) => !SKILL_TAGS.includes(tag) && !tag.includes(':'));
  const topics = labels.filter((l) => l !== 'academic' && l !== 'general-training');
  return (
    <div className="grid gap-3">
      <b>Topic</b>
      <TextField
        label="Topics and accent (separate with commas)"
        value={topics.join(', ')}
        disabled={disabled}
        hint="Lowercase letters, numbers, spaces and dashes. For example: birds, british."
        onChange={(event) => {
          const typed = event.target.value
            .split(',')
            .map((t) => t.trim().toLowerCase())
            .filter(Boolean);
          onChange([
            ...labels.filter((l) => l === 'academic' || l === 'general-training'),
            ...typed,
          ]);
        }}
      />
    </div>
  );
}

function ExplanationFields({
  skills,
  passage,
  canWrite,
  value,
  difficulty,
  onChange,
}: {
  skills: readonly string[];
  passage: string;
  canWrite: boolean;
  value: Record<string, unknown>;
  difficulty: string;
  onChange: (explanation: Record<string, unknown>, difficulty: string) => void;
}) {
  const text = (key: string) => (typeof value[key] === 'string' ? value[key] : '');
  const set = (key: string, next: string) => {
    onChange({ ...value, [key]: next }, difficulty);
  };
  const marked = skills.includes('writing') || skills.includes('speaking');
  return (
    <div className="grid gap-4">
      <SelectField
        label="Level (1 easiest, 9 hardest)"
        value={difficulty}
        onChange={(event) => {
          onChange(value, event.target.value);
        }}
      >
        <option value="">Not set</option>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((level) => (
          <option key={level} value={level}>
            {level}
          </option>
        ))}
      </SelectField>
      {marked ? (
        <TextAreaField
          label="Model answer (shown to the student after marking)"
          rows={6}
          value={text('modelAnswer')}
          onChange={(event) => {
            set('modelAnswer', event.target.value);
          }}
        />
      ) : (
        <>
          <EvidencePicker
            text={passage}
            quote={text('quote')}
            labelled={skills.includes('reading')}
            disabled={!canWrite}
            onPick={(quote) => {
              set('quote', quote);
            }}
          />
          <TextField
            label="Evidence: the exact words in the text that prove the answer"
            value={text('quote')}
            onChange={(event) => {
              set('quote', event.target.value);
            }}
          />
          <TextAreaField
            label="Why this is the answer (shown after marking)"
            rows={3}
            value={text('text')}
            onChange={(event) => {
              set('text', event.target.value);
            }}
          />
        </>
      )}
    </div>
  );
}
